/**
 * ControlBar Component
 * Compact horizontal bar with date picker, location selector, and day navigation
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Home, MapPin, Search, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import DateField from './DateField';
import TimeField from './TimeField';
import { LocationData } from '../LocationSearch/LocationSearch';
import { WORLD_CITIES } from '../LocationSearch/LocationSearch';
import { panchangaApi } from '../../services/api';
import type { CitySearchResult } from '../../types/api';

interface ControlBarProps {
  selectedDate: Date;
  selectedLocation: LocationData | null;
  onDateChange: (date: Date) => void;
  onLocationChange: (location: LocationData | null) => void;
  showNavigation?: boolean;
  showTimePicker?: boolean;
  showLocationPicker?: boolean;
  selectedTime?: string;
  onTimeChange?: (time: string) => void;
  onNowClick?: () => void;
  utcClock?: string;
  /** The day's sunrise and sunset ("HH:MM"), for the time picker's quick picks. */
  sunrise?: string;
  sunset?: string;
  showUjjainToggle?: boolean;
  ujjainReadingChecked?: boolean;
  onUjjainReadingChange?: (checked: boolean) => void;
  className?: string;
}

export const ControlBar: React.FC<ControlBarProps> = ({
  selectedDate,
  selectedLocation,
  onDateChange,
  onLocationChange,
  showNavigation = true,
  showTimePicker = false,
  showLocationPicker = true,
  selectedTime,
  onTimeChange,
  onNowClick,
  utcClock,
  sunrise,
  sunset,
  showUjjainToggle = false,
  ujjainReadingChecked = true,
  onUjjainReadingChange,
  className,
}) => {
  const [locationSearch, setLocationSearch] = useState('');
  const [isLocationOpen, setIsLocationOpen] = useState(false);
  const [apiResults, setApiResults] = useState<CitySearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const locationRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Check if selected date is today
  const isToday = () => {
    const today = new Date();
    return (
      today.getFullYear() === selectedDate.getFullYear() &&
      today.getMonth() === selectedDate.getMonth() &&
      today.getDate() === selectedDate.getDate()
    );
  };

  // Debounced API city search
  const searchCities = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.length < 2) {
      setApiResults([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await panchangaApi.searchCities(query, 10);
        setApiResults(results);
      } catch {
        setApiResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  }, []);

  // Show hardcoded cities when no search, API results when searching
  const displayCities = locationSearch.trim().length >= 2
    ? apiResults.map(city => ({
        id: `${city.latitude}_${city.longitude}`,
        name: city.name,
        state: city.state || '',
        country: city.country || '',
        latitude: city.latitude,
        longitude: city.longitude,
        altitude: city.altitude || 0,
      } as LocationData))
    : WORLD_CITIES.slice(0, 10);

  // Handle click outside for location dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (locationRef.current && !locationRef.current.contains(event.target as Node)) {
        setIsLocationOpen(false);
        setLocationSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    onDateChange(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    onDateChange(newDate);
  };

  const handleToday = () => {
    onDateChange(new Date());
  };

  const handleLocationSelect = (location: LocationData) => {
    onLocationChange(location);
    setIsLocationOpen(false);
    setLocationSearch('');
    setApiResults([]);
  };

  return (
    <div className={cn('kg-warm w-full px-0 sm:px-4 py-2.5', className)}>
      {/* No inner gutter on a phone: the page container already pads it (B-153). */}
      <div className="container mx-auto max-w-[1600px]">
        {/* All controls centered in a single row */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {/* Where, then when: location first, then the date between its day arrows (B-153). */}
          {/* Location Selector — API-powered city search */}
          {showLocationPicker && (
          <div className="relative" ref={locationRef}>
            <button
              type="button"
              onClick={() => setIsLocationOpen(!isLocationOpen)}
              aria-expanded={isLocationOpen}
              aria-haspopup="listbox"
              aria-label={selectedLocation ? `Location: ${selectedLocation.name}, ${selectedLocation.country}` : 'Select location'}
              className="kg-pill flex h-[34px] min-w-[140px] sm:min-w-[160px] max-w-[220px] items-center gap-1.5 rounded-[9px] px-3.5 text-[13.5px]"
            >
              <MapPin className="h-4 w-4 flex-shrink-0" style={{ color: 'var(--kg-sub)' }} />
              <span className="truncate">
                {selectedLocation
                  ? `${selectedLocation.name}, ${selectedLocation.country}`
                  : 'Select location'}
              </span>
            </button>

            {/* Location Dropdown */}
            {isLocationOpen && (
              <div
                className="absolute top-full left-0 z-50 mt-1.5 w-[min(320px,calc(100vw-2rem))] overflow-hidden rounded-[11px]"
                style={{ background: 'var(--kg-card)', border: '1px solid var(--kg-line)', boxShadow: 'var(--kg-shadow)' }}
              >
                {/* Search Input */}
                <div className="p-2" style={{ borderBottom: '1px solid var(--kg-line)' }}>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search any city worldwide..."
                      aria-label="Search cities"
                      value={locationSearch}
                      onChange={(e) => {
                        setLocationSearch(e.target.value);
                        searchCities(e.target.value);
                      }}
                      className="w-full pl-8 pr-8 py-1.5 text-sm rounded focus:outline-none focus:ring-2 focus:ring-[color:var(--kg-accent)]"
                      style={{ background: 'var(--kg-panel)', border: '1px solid var(--kg-line)', color: 'var(--kg-ink)' }}
                      autoFocus
                    />
                    {locationSearch && (
                      <button
                        type="button"
                        onClick={() => { setLocationSearch(''); setApiResults([]); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* City List */}
                <div className="max-h-[300px] overflow-auto" style={{ background: 'var(--kg-card)' }}>
                  {isSearching && displayCities.length === 0 && (
                    <div className="px-3 py-3 text-sm text-muted-foreground text-center">Searching...</div>
                  )}
                  {displayCities.map((city, index) => {
                    const isSelected =
                      selectedLocation?.latitude === city.latitude &&
                      selectedLocation?.longitude === city.longitude;
                    return (
                      <button
                        key={`${city.latitude}-${city.longitude}-${index}`}
                        type="button"
                        onClick={() => handleLocationSelect(city)}
                        className={cn(
                          'kg-row w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                          isSelected && 'font-medium'
                        )}
                        style={isSelected ? { background: 'var(--kg-greenbg)' } : undefined}
                      >
                        <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="truncate">
                            <span className="font-medium">{city.name}</span>
                            {city.state && <span className="text-muted-foreground">, {city.state}</span>}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {city.country}
                            {locationSearch.trim().length >= 2 && (
                              <span className="ml-1">({city.latitude.toFixed(2)}, {city.longitude.toFixed(2)})</span>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!isSearching && locationSearch.trim().length >= 2 && displayCities.length === 0 && (
                    <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                      No cities found
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          )}

          {/* The date, its day arrows and Today wrap as one group */}
          <div className="flex items-center gap-2">
          {/* Day Navigation - Previous */}
          {showNavigation && (
            <button
              type="button"
              onClick={handlePreviousDay}
              aria-label="Previous day"
              className="kg-square flex h-[34px] w-[34px] items-center justify-center rounded-[9px]"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}

          {/* Date: the site's calendar with a mouse, the device's picker on touch */}
          <DateField value={selectedDate} onChange={onDateChange} />

          {/* Day Navigation - Next */}
          {showNavigation && (
            <button
              type="button"
              onClick={handleNextDay}
              aria-label="Next day"
              className="kg-square flex h-[34px] w-[34px] items-center justify-center rounded-[9px]"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
          {/* Today Button */}
          {showNavigation && !isToday() && (
            <button
              type="button"
              onClick={handleToday}
              className="kg-accent-btn flex h-[34px] items-center gap-1 rounded-[9px] px-3.5 text-[13.5px] font-semibold"
            >
              <Home className="h-4 w-4" />
              Today
            </button>
          )}

          </div>

          {/* Time Picker — for Kāla Chakra view */}
          {showTimePicker && onTimeChange && (
            <div className="flex items-center gap-2">
              <TimeField value={selectedTime} onChange={onTimeChange} sunrise={sunrise} sunset={sunset} place={selectedLocation ? `${selectedLocation.name}, ${selectedLocation.country}` : undefined} />
              {onNowClick && (
                <button
                  type="button"
                  onClick={onNowClick}
                  className="kg-accent-btn flex h-[34px] items-center rounded-[9px] px-3.5 text-[13.5px] font-semibold"
                >
                  Now
                </button>
              )}
              {utcClock && (
                <span className="font-mono text-[13px] whitespace-nowrap" style={{ color: 'var(--kg-faint)' }}>
                  UTC:{' '}
                  <span className="font-semibold" style={{ color: 'var(--kg-ink)' }}>
                    {utcClock}
                  </span>
                </span>
              )}
            </div>
          )}

          {/* Ujjain Jyotiṣa Dṛṣṭi toggle — for the merged Kāla Chakra view */}
          {showUjjainToggle && onUjjainReadingChange && (
            <>
            <span aria-hidden className="mx-1 hidden h-5 w-px shrink-0 self-center xl:block" style={{ background: 'var(--kg-line)' }} />
            <label className="flex h-[34px] items-center gap-2 cursor-pointer text-[14px] font-medium whitespace-nowrap" style={{ color: 'var(--kg-ink)' }}>
              <input
                type="checkbox"
                checked={!!ujjainReadingChecked}
                onChange={(e) => onUjjainReadingChange(e.target.checked)}
                style={{ accentColor: '#e8951f', cursor: 'pointer', width: 17, height: 17 }}
                aria-label="Show Ujjain's Jyotiṣa Dṛṣṭi"
              />
              <span>Show Ujjain&rsquo;s Jyotiṣa Dṛṣṭi</span>
            </label>
            </>
          )}

        </div>

      </div>
    </div>
  );
};

export default ControlBar;
